import React, { useState, useEffect, useMemo } from 'react';
import { getReports, getAssets, openCustomerReport } from '../../services/api';
import { toast } from 'sonner';
import { PageHeader } from '../../components/Shared';
import { FileText, ArrowRight } from 'lucide-react';
import { useSortableList } from '../../hooks/useSortableList';
import { SortHeader, fmtDate } from '../../components/SortHeader';

const COMPARATORS = {
  id:        (r) => r.id || '',
  type:      (r) => (r.type || '').toLowerCase(),
  asset:     (r) => (r._assetName || '').toLowerCase(),
  engineer:  (r) => (r.engineer || '').toLowerCase(),
  issuedAt:  (r) => r.issuedAt || '',
};

const CustomerReports = () => {
  const [reports, setReports] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null);

  useEffect(() => {
    Promise.all([getReports(), getAssets().catch(() => [])])
      .then(([r, a]) => { setReports(r); setAssets(a); })
      .finally(() => setLoading(false));
  }, []);

  const openReport = async (r) => {
    setOpening(r.id);
    try {
      await openCustomerReport(r);
    } catch (e) {
      toast.error(e.message || 'Could not open report');
    } finally {
      setOpening(null);
    }
  };

  const assetById = useMemo(() => Object.fromEntries(
    (assets || []).map(a => [a.id, a]),
  ), [assets]);

  const enriched = useMemo(() => reports.map(r => ({
    ...r, _assetName: assetById[r.assetId]?.name || '',
  })), [reports, assetById]);

  const { sorted, sort, onSort } = useSortableList(enriched, COMPARATORS, {
    initialKey: 'issuedAt',
    initialDir: 'desc',
    defaultDescKeys: ['issuedAt'],
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="customer-reports-page">
      <PageHeader title="Reports" description="All technical reports and certificates for your sites." />
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">
            <FileText className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            No reports yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <SortHeader label="Ref" sortKey="id" sort={sort} onSort={onSort} />
                <SortHeader label="Type" sortKey="type" sort={sort} onSort={onSort} />
                <SortHeader label="Asset" sortKey="asset" sort={sort} onSort={onSort} />
                <SortHeader label="Engineer" sortKey="engineer" sort={sort} onSort={onSort} />
                <SortHeader label="Issued" sortKey="issuedAt" sort={sort} onSort={onSort} />
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map(r => (
                <tr
                  key={r.id}
                  onClick={() => openReport(r)}
                  className={`hover:bg-slate-50 cursor-pointer ${opening === r.id ? 'opacity-60' : ''}`}
                  data-testid={`report-row-${r.id}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.id}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{r.type || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{r._assetName || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{r.engineer || '—'}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(r.issuedAt)}</td>
                  <td className="px-4 py-3"><ArrowRight className="h-4 w-4 text-slate-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CustomerReports;
